import { supabase } from './supabaseClient';
import { clientToken } from './clientAccess';
import { stableValue } from './editorDraft';
import { isAcceptedQuote, parseQuoteObject, quoteItems, quoteNumber, quoteStaffing, staffingPolicy } from './quoteCalculations';

export const requiresSavedCalculationReview = (quote: any) => !isAcceptedQuote(quote) &&
  (quoteStaffing(quote).needsReview || quoteItems(quote?.items).some(i => quoteNumber(i.discount) !== 0));

const time = (value: any) => {
  if (value === undefined || value === null || String(value).trim() === '') return null;
  const raw = String(value).trim();
  const match = /^(\d{1,2}):([0-5]\d)(?::[0-5]\d(?:\.\d+)?)?$/.exec(raw);
  return match && Number(match[1]) < 24 ? `${match[1].padStart(2, '0')}:${match[2]}` : raw;
};

// The existing qcalc-v2/schema-2 snapshot; no change to fingerprint semantics.
export const savedCalculationSnapshot = (quote: any) => ({
  items: quoteItems(quote?.items).map(i => ({ id: i.id, price: quoteNumber(i.price), quantity: quoteNumber(i.quantity),
    discount: quoteNumber(i.discount), staffRequired: quoteNumber(i.staffRequired), isOptional: !!i.isOptional, selected: false })),
  staffCountRequired: quoteNumber(quote?.staff_count_required ?? quote?.staffCountRequired),
  staffingTier: quote?.staffing_tier ?? quote?.staffingTier ?? null,
  quoteStaffing: staffingPolicy(quote), setupTime: time(quote?.setup_time ?? quote?.setupTime),
  packDownTime: time(quote?.pack_down_time ?? quote?.packDownTime),
  internalCosts: parseQuoteObject(quote?.internal_costs ?? parseQuoteObject(quote?.logistics).internalCosts),
});
export const savedCalculationFingerprint = (quote: any) => {
  const text = stableValue(savedCalculationSnapshot(quote));
  let hash = 2166136261;
  for (let i = 0; i < text.length; i++) { hash ^= text.charCodeAt(i); hash = Math.imul(hash, 16777619); }
  return `qcalc-v2-${(hash >>> 0).toString(16).padStart(8, '0')}`;
};

export const publicApprovalMessage = (error: any) => {
  const message = String(error?.message || '');
  if (/REVIEW_REQUIRED|Calculation review required|OPTION_SELECTION_REQUIRES_REVIEW/.test(message)) {
    return 'Quikpix needs to check this proposal before it can be approved. Please contact Quikpix; you do not need to log in.';
  }
  if (/CLIENT_ACCESS_DENIED|permission denied/.test(message)) return 'This client link is invalid, expired or no longer available. Please contact Quikpix for a new link.';
  if (/EXPIRED/.test(message)) return 'This proposal has expired. Please contact Quikpix for an updated quote.';
  if (/NOT_FOUND|NOT_APPROVABLE|QUOTE_CHANGED/.test(message)) return 'This proposal is no longer available or has changed. Please reopen your link or contact Quikpix.';
  if (/SIGNATURE_REQUIRED/.test(message)) return 'Please enter your full name and confirm that you agree to the quote terms.';
  return "We couldn't complete the approval. Please try again or contact Quikpix.";
};
export class ClientApprovalError extends Error {}

export const approveClientQuote = async (quote: any, signerName: string, selected: Set<string>, blueprint: any[], packingList: string[]) => {
  const requestId = crypto.randomUUID();
  const { data, error, status } = await supabase.rpc('approve_client_quote_authorized', {
    p_client_token: clientToken(),
    p_quote_id: quote.id, p_signer_name: signerName, p_request_id: requestId,
    p_expected_updated_at: quote.updated_at ?? null, p_selected_item_ids: Array.from(selected),
    p_blueprint: blueprint, p_packing_list: packingList,
  });
  if (error) {
    console.error('Client quote approval failed', { requestId, quoteId: quote.id, rpc: 'approve_client_quote_authorized',
      httpStatus: status, code: error.code, message: error.message, details: error.details, hint: error.hint });
    throw new ClientApprovalError(publicApprovalMessage(error));
  }
  return data;
};

export const currentQuoteApprovalState = async (quoteId: string) => {
  const { data, error } = await supabase.rpc('client_quote_approval_state_authorized', { p_quote_id: quoteId, p_client_token: clientToken() });
  if (error) throw error;
  return data;
};

export const reviewSavedQuoteForClient = async (quoteId: string) => {
  const { data: quote, error } = await supabase.from('quotes').select('*').eq('id', quoteId).single();
  if (error) throw error;
  if (!window.confirm(`Explicitly review the saved staffing policy and percentage discounts for “${quote.title}”?\nSubtotal: $${Number(quote.subtotal).toFixed(2)}\nAccepted total including GST: $${Number(quote.grand_total ?? quote.total_amount).toFixed(2)}\nOnly confirm after checking these calculations. This records your Admin review; it does not change the quote totals.`)) return false;
  const { error: reviewError } = await supabase.rpc('admin_review_quote_for_client', {
    p_quote_id: quote.id, p_snapshot: savedCalculationSnapshot(quote), p_fingerprint: savedCalculationFingerprint(quote),
  });
  if (reviewError) throw reviewError;
  return true;
};
